/**
 * Text measurement abstraction.
 *
 * The layout solver needs intrinsic text width + line metrics without a
 * DOM. `Measure` is the seam:
 *
 *   - Browser: back it with `canvas measureText` (accurate).
 *   - Headless (Node): back it with per-font advance-width tables.
 *
 * The kernel ships `heuristicMeasure` — a deterministic estimator good
 * enough to prove the pipeline and write golden tests. Real metric
 * tables are a phase-1 task (see the plan doc); swapping them in changes
 * only the numbers, not the architecture.
 */
import type { FontSpec, TextRun } from "./model.js";

export interface TextMetrics {
  readonly width: number;
  readonly ascent: number;
  readonly descent: number;
  readonly lineHeight: number;
}

export interface Measure {
  text(s: string, font: FontSpec): TextMetrics;
}

// Average advance width as a fraction of font size, per broad family
// class. Crude but stable; replaced by real tables in phase 1.
function avgRatio(family: string): number {
  return /mono/i.test(family) ? 0.6 : 0.52;
}

export const heuristicMeasure: Measure = {
  text(s, font) {
    const ratio = avgRatio(font.family);
    // Heavier weights are marginally wider.
    const weightBump = font.weight >= 700 ? 1.05 : 1;
    return {
      width: s.length * font.size * ratio * weightBump,
      ascent: font.size * 0.8,
      descent: font.size * 0.2,
      lineHeight: font.size * 1.25,
    };
  },
};

/**
 * Truncate `s` with an ellipsis so it fits `maxWidth` under `font`.
 * Returns the original string when it already fits.
 */
export function ellipsize(
  s: string,
  maxWidth: number,
  font: FontSpec,
  measure: Measure,
): string {
  if (measure.text(s, font).width <= maxWidth) return s;
  const ell = "…";
  const ellW = measure.text(ell, font).width;
  if (ellW > maxWidth) return "";
  let lo = 0;
  let hi = s.length;
  // Largest prefix whose width + ellipsis fits.
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    const w = measure.text(s.slice(0, mid), font).width + ellW;
    if (w <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return s.slice(0, lo).trimEnd() + ell;
}

/**
 * Split an unbreakable token into its intra-word break opportunities.
 *
 * Identifier-shaped labels (`UmpКредиты_ММБ_Кредитная_заявка_Продукт`,
 * `Orders.placeOrder`) carry no whitespace, so a whitespace-only wrap has
 * nowhere to break and falls back to a mid-glyph character break. These are the
 * seams a reader already sees:
 *
 *   - after a separator run (`_ - . / : ,`) — the separator STAYS on the
 *     leading chunk, so the break reads as "…_" / "…." at the line end;
 *   - before a lower→Upper camel boundary (`создатьЗаявку` → `создать|Заявку`),
 *     the same seam `splitCasedName` humanizes on.
 *
 * Returns `[word]` when the token has no seam.
 */
export function segmentAtSeams(word: string): string[] {
  const segs: string[] = [];
  let cur = "";
  const chars = Array.from(word);
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i]!;
    const prev = chars[i - 1];
    // Camel seam: a lowercase/digit followed by an uppercase starts a segment.
    if (cur && prev && /[\p{Ll}\p{Nd}]/u.test(prev) && /\p{Lu}/u.test(ch)) {
      segs.push(cur);
      cur = "";
    }
    cur += ch;
    // Separator seam: break AFTER the last separator of a run.
    if (/[_\-./:,]/.test(ch) && !(chars[i + 1] && /[_\-./:,]/.test(chars[i + 1]!))) {
      segs.push(cur);
      cur = "";
    }
  }
  if (cur) segs.push(cur);
  return segs.length ? segs : [word];
}

/**
 * Greedy word-wrap `s` to fit `maxWidth`. A word longer than the line is broken
 * at its intra-word seams (see {@link segmentAtSeams}) and, only if a single
 * seam-segment still overflows, hard-broken by character. When `maxLines` is
 * given and more text remains, the last kept line is ellipsized.
 *
 * `firstLineWidth` (default `maxWidth`) narrows ONLY the first line — for a
 * leading inline glyph that steals room before the text (e.g. the swimlane
 * START-trigger `▶` prefix reserved by `TRIGGER_MARKER_W`). Every line after
 * the first uses the full `maxWidth`.
 */
export function wrapText(
  s: string,
  maxWidth: number,
  font: FontSpec,
  measure: Measure,
  maxLines = Infinity,
  firstLineWidth = maxWidth,
): string[] {
  if (maxWidth <= 0 || !s) return s ? [s] : [];
  const words = s.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  // Budget for the line currently being built: the first line may be narrower
  // (leading-marker reservation); once it's committed, the rest use `maxWidth`.
  const curWidth = () => (lines.length === 0 ? firstLineWidth : maxWidth);
  const fits = (str: string) => measure.text(str, font).width <= curWidth();

  const pushWordBroken = (word: string) => {
    // Word doesn't fit on its own line — break by character.
    let chunk = "";
    for (const ch of word) {
      if (chunk && !fits(chunk + ch)) {
        lines.push(chunk);
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    line = chunk;
  };

  // Word doesn't fit whole — flow its seam segments, char-breaking only a
  // segment that still overflows on its own. `line` starts empty (the caller
  // flushed it), so segments glue without a space.
  const pushWordSegmented = (word: string) => {
    const segs = segmentAtSeams(word);
    if (segs.length < 2) {
      pushWordBroken(word);
      return;
    }
    for (const seg of segs) {
      if (fits(line + seg)) {
        line += seg;
        continue;
      }
      if (line) {
        lines.push(line);
        line = "";
      }
      if (fits(seg)) line = seg;
      else pushWordBroken(seg);
    }
  };

  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (fits(candidate)) {
      line = candidate;
    } else {
      if (line) lines.push(line);
      line = "";
      if (fits(word)) line = word;
      else {
        pushWordSegmented(word);
      }
    }
  }
  if (line) lines.push(line);

  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    const last = kept.length - 1;
    // Force a trailing ellipsis to signal truncation: append it if it fits,
    // otherwise shrink the line to make room. A seam break leaves the
    // separator at the line end (`Продукт.`), which would read as `Продукт.…`
    // — drop it so the ellipsis is the only terminator.
    const withEll = kept[last]!.replace(/[_\-./:,\s]+$/, "") + "…";
    kept[last] = fits(withEll) ? withEll : ellipsize(kept[last]!, maxWidth, font, measure);
    return kept;
  }
  return lines;
}

/** A styled span within a wrapped rich line; `spaceBefore` is false when
 *  it glues to the previous span (e.g. punctuation after an italic word).
 *  `x`/`w` are the span's measured offset + width from the line start —
 *  used by the SVG backend to position tspans + draw inline-code pills. */
export interface RichSpan extends TextRun {
  readonly spaceBefore?: boolean;
  readonly x?: number;
  readonly w?: number;
}
/** A wrapped line of rich text: contiguous same-style spans, left to right. */
export type RichLine = readonly RichSpan[];

/** Effective font for a run against a base font (bold bumps weight, code swaps to mono). */
export function runFont(base: FontSpec, run: TextRun, monoFamily?: string): FontSpec {
  const weight = run.bold ? Math.max(base.weight, 700) : base.weight;
  const family = run.code && monoFamily ? monoFamily : base.family;
  return weight === base.weight && family === base.family ? base : { ...base, weight, family };
}

interface Tok extends TextRun {
  sp: boolean; // whitespace precedes this token in the source
}

/**
 * Greedy word-wrap a run list to `maxWidth`, preserving each word's
 * bold/italic AND the original inter-word spacing (so punctuation that
 * hugged a styled word stays glued). Returns lines of merged same-style
 * spans. `maxLines` truncates and ellipsizes the last line.
 */
export function wrapRuns(
  runs: readonly TextRun[],
  maxWidth: number,
  base: FontSpec,
  measure: Measure,
  maxLines = Infinity,
  monoFamily?: string,
): RichLine[] {
  const spaceW = measure.text(" ", base).width;
  // Tokenize, tracking whether a space separated each word in the source —
  // spaces live in the plain runs around bold/italic spans.
  const toks: Tok[] = [];
  let prevWs = false;
  for (const r of runs) {
    if (r.glyph) {
      // An inline glyph is a single, unbreakable token; a space follows it.
      toks.push({ ...r, text: "", sp: toks.length > 0 && prevWs });
      prevWs = true;
      continue;
    }
    const lead = /^\s/.test(r.text);
    const words = r.text.split(/\s+/).filter(Boolean);
    words.forEach((w, i) => {
      const sp = toks.length === 0 ? false : i > 0 ? true : prevWs || lead;
      toks.push({ ...r, text: w, sp });
    });
    prevWs = words.length ? /\s$/.test(r.text) : prevWs || /\s/.test(r.text);
  }
  if (!toks.length || maxWidth <= 0) return toks.length ? [toks] : [];

  const lines: Tok[][] = [];
  let line: Tok[] = [];
  let lineW = 0;
  for (const tk of toks) {
    const wW = tk.glyph ? tk.glyph.width : measure.text(tk.text, runFont(base, tk, monoFamily)).width;
    const add = (line.length && tk.sp ? spaceW : 0) + wW;
    if (line.length && lineW + add > maxWidth) {
      lines.push(line);
      line = [{ ...tk, sp: false }]; // first on a new line: no leading space
      lineW = wW;
    } else {
      line.push(tk);
      lineW += add;
    }
  }
  if (line.length) lines.push(line);

  const capped = lines.length > maxLines ? lines.slice(0, maxLines) : lines;
  if (lines.length > maxLines && capped.length) {
    const last = capped[capped.length - 1]!;
    const tail = last[last.length - 1]!;
    capped[capped.length - 1] = [...last.slice(0, -1), { ...tail, text: tail.text + "…" }];
  }
  // Merge adjacent same-style tokens into spans, honoring each token's
  // space flag so glued punctuation doesn't gain a space.
  const sameStyle = (a: Tok, b: Tok) =>
    !a.glyph && !b.glyph &&
    !!a.bold === !!b.bold && !!a.italic === !!b.italic && !!a.strike === !!b.strike &&
    !!a.code === !!b.code && !!a.dim === !!b.dim && a.href === b.href;
  return capped.map((ws) => {
    const spans: RichSpan[] = [];
    let prevTok: Tok | undefined;
    for (const w of ws) {
      if (prevTok && sameStyle(prevTok, w)) {
        const last = spans[spans.length - 1]!;
        spans[spans.length - 1] = { ...last, text: last.text + (w.sp ? " " : "") + w.text };
      } else {
        const { sp, ...run } = w;
        spans.push({ ...run, spaceBefore: w.sp });
      }
      prevTok = w;
    }
    // Measure each span's offset + width along the line (for SVG positioning).
    let cursor = 0;
    return spans.map((s, i) => {
      if (i > 0 && s.spaceBefore !== false) cursor += spaceW;
      const w = s.glyph ? s.glyph.width : measure.text(s.text, runFont(base, s, monoFamily)).width;
      const placed = { ...s, x: cursor, w };
      cursor += w;
      return placed;
    });
  });
}
